import { useState } from "react";
import { SERVICES } from "@/data/content";
import {
  Globe,
  ShoppingBag,
  LayoutTemplate,
  Search,
  MapPinned,
  Magnet,
  Mail,
  MessageCircle,
  Users,
  Bot,
  GitBranch,
  Settings2,
  Workflow,
  ArrowUpRight,
} from "lucide-react";

const ICONS = {
  web: Globe,
  woo: ShoppingBag,
  landing: LayoutTemplate,
  seo: Search,
  localseo: MapPinned,
  leads: Magnet,
  email: Mail,
  whatsapp: MessageCircle,
  crm: Users,
  ai: Bot,
  n8n: GitBranch,
  make: Workflow,
  bpa: Settings2,
};

const TABS = ["All", "Build", "Automate", "Grow"];

export default function Services() {
  const [tab, setTab] = useState("All");
  const list =
    tab === "All" ? SERVICES : SERVICES.filter((s) => s.group === tab);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 bg-[color:var(--bg-soft)]/60 border-b hairline relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="section-label mb-4">
              <span className="dot" /> Services
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
              Everything a growing business needs — under{" "}
              <span className="font-serif-accent italic font-normal">
                one roof
              </span>
              .
            </h2>
          </div>

          <div
            data-testid="services-tabs"
            className="flex flex-wrap items-center gap-2 p-1 rounded-full border hairline bg-white/60 backdrop-blur"
          >
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`services-tab-${t.toLowerCase()}`}
                className={`text-sm px-4 py-2 rounded-full transition-colors ${
                  tab === t
                    ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                    : "text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[color:var(--hair)] border hairline rounded-lg overflow-hidden">
          {list.map((s) => {
            const Icon = ICONS[s.key] || Globe;
            return (
              <article
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="group p-7 md:p-8 bg-[color:var(--bg)] hover:bg-white transition-colors flex flex-col justify-between min-h-[230px]"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-md border hairline grid place-items-center bg-white">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--ink-2)]">
                      {s.group}
                    </span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-6">
                    {s.title}
                  </h3>
                  <p className="text-sm md:text-base text-[color:var(--ink-2)] mt-2 leading-relaxed">
                    {s.short}
                  </p>
                </div>
                <div className="mt-6 flex items-end justify-between">
                  <ul className="text-xs text-[color:var(--ink-2)] space-y-1">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2">
                        <span
                          className="inline-block w-1 h-1 rounded-full"
                          style={{ background: "var(--ink-2)" }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    aria-label={`Enquire about ${s.title}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-6 text-sm text-[color:var(--ink-2)]">
          Looking for something custom? We also handle integrations, data
          pipelines, and bespoke internal tools. <a className="underline" href="#contact" data-testid="services-custom-link">Talk to us →</a>
        </p>
      </div>
    </section>
  );
}
